import React, { useState } from 'react';
import * as Linking from 'expo-linking';
import Stack from '../../../../../../v2BaseComponents/Stack';
import { ActionButtonLayout } from '../../../../../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../../../../../utils/intl';
import Text from '../../../../../../v2BaseComponents/Text';
import Icon from '../../../../../../v2BaseComponents/Icon';
import MainContentContainer from '../../../../../../v2BaseComponents/MainContentContainer';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import useOrganizationPaymentIntegration from '../../../../../../../hooks/api/useOrganizationPaymentIntegration';
import usePreRedirectArgs from '../../../../../../../hooks/usePreRedirectArgs';
import { useIsOrganizationOwner } from '../../../../../../../hooks/api/useOrganization';
import { useIsE2E } from '../../../../../../../hooks/useIsE2E';
import Alert from '../../../../../../v2BaseComponents/Alert';

const intl = getIntl('client-billing-tab-not-setup', {
    header: 'Collect Payment from Clients',
    subHeader:
        "Primary Record integrates with Stripe to facilitate invoicing and payment collection from your clients.  To get started, connect to stripe.\n\nYou'll be redirected to Stripe, our payment partner, to complete a brief verification process. Stripe will collect and securely store your information using bank-level security standards. This helps prevent fraud and comply with government regulations.\n\nPrimary Record does not store or have access to the verification details you provide to Stripe.\n\nOnce onboarded, client invoicing will be enabled.",
    orgOwnerSubtext: 'Your organization owner must connect to Stripe to enable client invoicing.',
    buttonText: 'Connect to Stripe',
    loaderMessage: 'Creating and connecting to your Stripe account...',
});

export type NotSetupContentOwnProps = {};

export type NotSetupContentProps = NotSetupContentOwnProps;

const NotSetupContent: React.FC<NotSetupContentProps> = (_props) => {
    const isE2E = useIsE2E();
    const [isConnecting, setIsConnecting] = useState(false);
    const isOrganizationOwner = useIsOrganizationOwner();
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const { isRetrievingSetupLink, retrieveSetupLink, setupTestOrganization } = useOrganizationPaymentIntegration({
        onRetrieveSetupLinkSuccess: (setupLink) => {
            setPreRedirectArgs(
                {
                    type: 'stripe-connected-account',
                },
                () => {
                    setIsConnecting(true);
                    Linking.openURL(setupLink);
                },
            );
        },
    });

    return (
        <AnimateLoadedContent
            isLoading={isRetrievingSetupLink || isConnecting}
            message={isConnecting ? intl('loaderMessage') : undefined}
        >
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={[
                        {
                            title: intl('buttonText'),
                            icon: 'Tool',
                            onPress: retrieveSetupLink,
                            variant: 'primary',
                            testID: 'setup-stripe-button',
                            isDisabled: !isOrganizationOwner,
                        },
                        {
                            title: 'Setup Test Organization',
                            icon: 'Tool',
                            onPress: setupTestOrganization,
                            variant: 'primary',
                            testID: 'setup-test-stripe-button',
                            isDisabled: !isOrganizationOwner,
                            show: isE2E,
                        },
                    ]}
                >
                    <MainContentContainer style={{ minHeight: '100%', minWidth: '100%' }}>
                        <Stack vertical justify="center" align="center" space={20} fullHeight fullWidth>
                            <Stack vertical justify="center" align="center" space={4}>
                                <Text variant="bold-h4">{intl('header')}</Text>
                                <Icon name="MoneyBag" size="alert-icon" />
                                {isOrganizationOwner ? (
                                    <Alert type="info" message={intl('subHeader')} title={intl('buttonText')} />
                                ) : (
                                    <Text variant="caption-2" alignText="center" color="Gray-Text">
                                        {intl('orgOwnerSubtext')}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(NotSetupContent);
